import React, { Component } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    TextField,
    Button,
    Grid,
    Paper,
    IconButton,
    styled
} from '@mui/material';

// Ícones
import {
    PersonOutline,
    ImageOutlined,
    CampaignOutlined,
    GridViewOutlined,
    FileUploadOutlined,
    Check,
    Launch,
    FormatSizeOutlined,
    PaletteOutlined,
    DeleteOutline // Adicionado para o botão de remover foto
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { apiUrl } from '../../comp/ApiUrl';
import { Link } from 'react-router-dom';

const DisplayOption = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    borderRadius: '16px',
    border: `2px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
    backgroundColor: selected ? 'rgba(103, 58, 183, 0.04)' : '#fff',
    transition: 'all 0.2s ease',
    boxShadow: 'none',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: selected ? '#673ab7' : '#e0e0e0',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
}));

export default class Appearance extends Component {
    static contextType = AuthContext;

    constructor(props) {
        super(props);
        this.state = {
            activeTab: 0,
            name: '',
            displayMode: 'logo-text',
            color: '#673ab7',
            photo: null, // Armazena a string da URL do backend ou a pré-visualização em Base64/Blob
            imageFile: null, // Armazena o arquivo File real para enviar via fetch
            isRemovingImage: false, // Flag caso decida salvar sem imagem
            loading: false, // Controle de feedback de envio
        };
        // Referência para o input de arquivo oculto
        this.fileInputRef = React.createRef();
    }

    componentDidMount() {
        if (this.context && this.context.user) {
            this.setState({
                name: this.context.user.store_name || '',
                color: this.context.user.store_color || '#673ab7',
                photo: this.context.user.store_photo || null
            });
        }
    }

    handleTabChange = (event, newValue) => {
        this.setState({ activeTab: newValue });
    };

    setDisplayMode = (mode) => {
        this.setState({ displayMode: mode });
    };

    // --- LÓGICA DE GERENCIAMENTO DE IMAGEM ---

    handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            this.setState({
                imageFile: file,
                photo: URL.createObjectURL(file), // Cria um link temporário para preview visual
                isRemovingImage: false
            });
        }
    };

    handleRemoveImage = () => {
        this.setState({
            imageFile: null,
            photo: null,
            isRemovingImage: true
        });
        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = ''; // Reseta o input file HTML
        }
    };

    // --- FUNÇÃO DE ENVIO PARA O BACKEND ---
    handleSave = async () => {
        this.setState({ loading: true });

        // Usamos FormData porque estamos enviando arquivos (Mutipart/form-data)
        const formData = new FormData();
        formData.append('store_name', this.state.name);
        formData.append('store_color', this.state.color);
        formData.append('display_mode', this.state.displayMode);

        if (this.state.imageFile) {
            // Se escolheu um novo arquivo
            formData.append('store_photo', this.state.imageFile);
        } else if (this.state.isRemovingImage) {
            // Se removeu a imagem anterior, mande uma flag ou vazio pro seu backend tratar
            formData.append('remove_photo', 'true');
        }

        try {
            const response = await fetch(`${apiUrl}/store/update-appearance`, {
                credentials: 'include',
                method: 'POST', // ou 'PUT' dependendo da sua API
                headers: {
                    // Importante: NÃO adicione 'Content-Type' aqui. O navegador faz isso automaticamente ao ver o FormData.
                    'Authorization': `Bearer ${this.context.token}` // Caso sua rota seja autenticada
                },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();

                // Atualiza o contexto global do app com os novos dados se a sua função permitir
                if (this.context.updateUser) {
                    this.context.updateUser(data.user);
                }

                this.setState({ isRemovingImage: false, imageFile: null, loading: false });
                alert('Alterações salvas com sucesso!');
            } else {
                throw new Error('Erro ao salvar alterações');
            }
        } catch (error) {
            console.error(error);
            alert('Houve um erro ao salvar as alterações.');
            this.setState({ loading: false });
        }
    };

    // --- VERIFICAR SE HOUVE ALTERAÇÃO ---
    hasChanges = () => {
        const { user } = this.context;
        if (!user) return false;

        const nameChanged = this.state.name !== (user.store_name || '');
        const colorChanged = this.state.color !== (user.store_color || '#673ab7');

        // Mudou se escolheu um novo arquivo OU se clicou em remover uma foto existente
        const photoChanged = this.state.imageFile !== null || this.state.isRemovingImage;

        return nameChanged || colorChanged || photoChanged;
    };

    render() {
        const { activeTab, displayMode, loading, photo } = this.state;
        const canSave = this.hasChanges() && !loading;

        return (
            <Box sx={{ p: 0, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="700" gutterBottom>
                            Aparência
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Personalize a identidade visual do seu catálogo
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <a href={`https://${this.context.user.store_domain}.fazpedido.app`} target="_blank" rel="noopener noreferrer">
                            <Button
                                variant="outlined"
                                startIcon={<Launch sx={{ fontSize: 18 }} />}
                                sx={{ borderRadius: '8px', textTransform: 'none', borderColor: '#e0e0e0', color: '#444' }}
                            >
                                Visualizar
                            </Button></a>
                        <Button
                            variant="contained"
                            disabled={!canSave} // Habilita somente se houver modificações
                            onClick={this.handleSave}
                            startIcon={<Check />}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                bgcolor: canSave ? '#673ab7 !important' : '#d1c4e9 !important',
                                color: canSave ? '#fff !important' : '#673ab7 !important'
                            }}
                        >
                            {loading ? 'Salvando...' : (canSave ? 'Salvar Alterações' : 'Salvo')}
                        </Button>
                    </Box>
                </Box>

                {/* Tabs */}
                <Box sx={{ mb: 4 }}>
                    <Tabs
                        value={window.location.pathname} // Usa a rota atual como valor para manter a tab sincronizada
                        sx={{
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                minHeight: '40px',
                                borderRadius: '8px',
                                mr: 1,
                                fontWeight: 500,
                                color: '#666'
                            },
                            '& .Mui-selected': {
                                bgcolor: '#fff',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                color: '#000 !important'
                            }
                        }}
                    >
                        <Tab
                            component={Link}
                            to="/appearance"
                            value="/appearance"
                            icon={<PersonOutline sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Identidade"
                        />
                        <Tab
                            component={Link}
                            to="banners"
                            value="banners"
                            icon={<ImageOutlined sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Banners"
                        />
                        <Tab
                            component={Link}
                            to="/avisos"
                            value="/avisos"
                            icon={<CampaignOutlined sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Avisos"
                        />
                        <Tab
                            component={Link}
                            to="/catalogo"
                            value="/catalogo"
                            icon={<GridViewOutlined sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Catálogo"
                        />
                    </Tabs>
                </Box>

                {/* Card Principal */}
                <Paper sx={{ p: 4, borderRadius: '16px', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Typography variant="h6" fontWeight="600">Identidade</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 4 }}>
                        Logo, cores e nome da loja
                    </Typography>

                    {/* Nome da Loja */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>Nome da loja</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={this.state.name}
                            placeholder='Ex: Loja de Roupas'
                            onChange={(e) => this.setState({ name: e.target.value })}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fcfcfc' } }}
                        />
                    </Box>

                    {/* Logo (Adicionar, Alterar e Remover) */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" fontWeight="500" sx={{ mb: 1 }}>Logo</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{
                                width: 64, height: 64, border: '2px dashed #e0e0e0', borderRadius: '12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc',
                                overflow: 'hidden'
                            }}>
                                {photo ? (
                                    <img
                                        src={photo.startsWith('blob:') ? photo : `${apiUrl}/store/photo/${photo}`}
                                        alt="Logo"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <ImageOutlined />
                                )}
                            </Box>

                            {/* Input Oculto de Arquivo */}
                            <input
                                type="file"
                                accept="image/*"
                                ref={this.fileInputRef}
                                style={{ display: 'none' }}
                                onChange={this.handleImageChange}
                            />

                            <Button
                                variant="outlined"
                                startIcon={<FileUploadOutlined />}
                                onClick={() => this.fileInputRef.current.click()}
                                sx={{ borderRadius: '8px', textTransform: 'none', color: '#444', borderColor: '#e0e0e0' }}
                            >
                                {photo ? 'Trocar Imagem' : 'Enviar'}
                            </Button>

                            {photo && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteOutline />}
                                    onClick={this.handleRemoveImage}
                                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                                >
                                    Remover
                                </Button>
                            )}
                        </Box>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                            PNG, JPG ou WebP. Máximo 2MB.
                        </Typography>
                    </Box>

                    {/* Cor Principal */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body2" fontWeight="500" sx={{ mb: 2 }}>Cor principal</Typography>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            {['#673ab7', '#e91e63', '#00c853', '#ff9100', '#2196f3', '#f44336'].map((colorItem) => (
                                <Box
                                    key={colorItem}
                                    onClick={() => this.setState({ color: colorItem })}
                                    sx={{
                                        width: 32, height: 32, bgcolor: colorItem, borderRadius: '50%', cursor: 'pointer',
                                        border: this.state.color === colorItem ? '3px solid #000' : 'none',
                                        boxShadow: this.state.color === colorItem ? '0 0 5px rgba(0,0,0,0.3)' : 'none',
                                        transition: 'all 0.1s ease'
                                    }}
                                />
                            ))}
                            <IconButton
                                size="small"
                                component="label"
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    bgcolor: '#fff'
                                }}
                            >
                                <PaletteOutlined fontSize="small" />
                                <input
                                    type="color"
                                    value={this.state.color} // Corrigido de "cor" para "color"
                                    onChange={(e) => {
                                        this.setState({ color: e.target.value });
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        opacity: 0,
                                        cursor: 'pointer'
                                    }}
                                />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Opções de Exibição */}
                    <Box>
                        <Typography variant="body2" fontWeight="500" sx={{ mb: 2 }}>Exibição no catálogo</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <DisplayOption
                                    selected={displayMode === 'logo-text'}
                                    onClick={() => this.setDisplayMode('logo-text')}
                                >
                                    <GridViewOutlined color="disabled" />
                                    <Typography variant="caption" color="text.secondary">Logo + texto</Typography>
                                </DisplayOption>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <DisplayOption
                                    selected={displayMode === 'logo-only'}
                                    onClick={() => this.setDisplayMode('logo-only')}
                                >
                                    <ImageOutlined color="disabled" />
                                    <Typography variant="caption" color="text.secondary">Somente logo</Typography>
                                </DisplayOption>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <DisplayOption
                                    selected={displayMode === 'text-only'}
                                    onClick={() => this.setDisplayMode('text-only')}
                                >
                                    <FormatSizeOutlined color="disabled" />
                                    <Typography variant="caption" color="text.secondary">Somente texto</Typography>
                                </DisplayOption>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Box>
        );
    }
}